import { CustomerDocument } from '@graphcommerce/magento-customer/hooks/Customer.gql'
import { useCustomerQuery } from '@graphcommerce/magento-customer/hooks/useCustomerQuery'
import { useCustomerSession } from '@graphcommerce/magento-customer/hooks/useCustomerSession'
import { useFormAutoSubmit, useFormGqlQuery } from '@graphcommerce/react-hook-form'
import { useEffect, useState } from 'react'
import { IsEmailAvailableDocument } from '../../graphql/IsEmailAvailable.gql'

export type UseFormIsEmailAvailableProps = {
  email?: string | null
  onSubmitted?: (data: { email: string }) => void
}

export function useFormIsEmailAvailable(props: UseFormIsEmailAvailableProps) {
  const { email, onSubmitted } = props
  const { loggedIn, requireAuth } = useCustomerSession()
  const customerQuery = useCustomerQuery(CustomerDocument)

  const form = useFormGqlQuery(
    IsEmailAvailableDocument,
    { mode: 'onChange', defaultValues: { email: email ?? '' } },
    { fetchPolicy: 'cache-and-network' },
  )
  const { formState, data, handleSubmit } = form

  const submit = handleSubmit(onSubmitted || (() => {}))
  const autoSubmitting = useFormAutoSubmit({ form, submit, forceInitialSubmit: true })

  const hasAccount = data?.isEmailAvailable?.is_email_available === false
  const isSubUser = data?.isEmailAvailable?.is_sub_user === true

  const { isDirty, isSubmitSuccessful, isSubmitted, isSubmitting, isValid } = formState

  const [mode, setMode] = useState<
    'email' | 'signin' | 'signup' | 'signedin' | 'session-expired' | 'forgot'
  >(loggedIn ? 'signedin' : 'email')

  useEffect(() => {
    if (loggedIn) {
      setMode('signedin')
      return
    }
    if (isSubmitting) return
    if (!isValid) return
    if (!isDirty && isSubmitted && isSubmitSuccessful && isValid)
      setMode(hasAccount ? 'signin' : 'signup')

    if (customerQuery.data?.customer && requireAuth)
      setMode(isSubmitSuccessful ? 'signin' : 'session-expired')
  }, [
    customerQuery.data?.customer,
    hasAccount,
    isDirty,
    isSubmitSuccessful,
    isSubmitted,
    isSubmitting,
    isValid,
    loggedIn,
    requireAuth,
  ])

  return { mode, form, submit, autoSubmitting, hasAccount, setMode, isSubUser }
}
